"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ssrStyles = exports.getSSRStyles = exports.getReactNativeWebSSRStyles = exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _AppRegistry = _interopRequireDefault(require("react-native-web/dist/cjs/exports/AppRegistry"));
var _inject = require("./ssr-media-query/utils/inject");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/** @typedef {import('react').ComponentType} ReactComponent */
/** @typedef {import('react').ReactElement} ReactElement */

/**
 * Returns object with `renderApp` and `getStyles` functions.
 * Weave these into your app's server-side render process:
 *
 * - Call `renderApp` first to do the actual server-side render
 * - After the render is complete, call `getStyles`
 * - Include the style tags returned by `getStyles` in the SSR <head>
 *
 * @param {string} [appName] - optional unique identifier if ssrStyles is called multiple times for multiple apps
 * @param {object} [options] -
 *  - `styleGetters`: optional array of additional style getter functions to call after render
 *  - `collectStyles`: optional function, takes the rendered app, returns either the same or a new app element
 * @param {boolean} [options.styleGetters]
 * @param {(ReactElement) => ReactElement} [options.collectStyles]
 */
const ssrStyles = function () {
  let appName = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'UDS app';
  let {
    styleGetters = [],
    collectStyles
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let hasAppRendered = false;
  return {
    /**
     * Server-side-renders the provided app in a way that supports collecting
     * styles for Styled Components and React Native Web stylesheets.
     *
     * @param {ReactComponent} App - the root component for the app
     * @param {object} [props] - props for this render e.g. page routing props
     * @param {object} [options] -
     *  - `renderedByRNW`: pass as true if the main render is by AppRegistry.runApplication from React Native
     *  - `WrapperComponent`: Component rendered with no props around app content and inside root tag
     * @param {boolean} [options.renderedByRNW]
     * @param {ReactComponent} [options.WrapperComponent]
     *
     * @returns {ReactElement} - the rendered app output
     */
    renderApp: function (App, props) {
      let {
        renderedByRNW = false,
        WrapperComponent
      } = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      _AppRegistry.default.registerComponent(appName, () => App);
      // AppRegistry.getApplication renders the app in a container, and collects styles.
      const {
        element,
        getStyleElement
      } = _AppRegistry.default.getApplication(appName, {
        WrapperComponent,
        initialProps: props
      });
      let renderedApp =
      // React Native Web's AppRegistry.getApplication assumes the app is rendered using
      // AppRegistry.runApplication and wraps it in <AppContainer>, which wraps the entire app
      // in two outer containers resembling <View style={{ flex: 1 }} pointerEvents="box-none">.
      // So, use that IF user says AppRegistry.runApplication will do the client-side render.
      renderedByRNW && element ||
      // If the live app is not rendered using AppRegistry.runApplication, we need to
      // re-render it without the <AppContainer> wrapper, to avoid SSR mismatch errors.
      // Default to this as many platforms (e.g. NextJS) will use their own renderers.
      WrapperComponent ? /*#__PURE__*/(0, _jsxRuntime.jsx)(WrapperComponent, {
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(App, {
          ...props
        })
      }) : /*#__PURE__*/(0, _jsxRuntime.jsx)(App, {
        ...props
      });
      const getRNWStyle = () => getStyleElement({
        key: 'react-native-stylesheet'
      });
      styleGetters.push(getRNWStyle);
      if (typeof collectStyles === 'function') {
        renderedApp = collectStyles(renderedApp);
      }
      hasAppRendered = true;
      return renderedApp;
    },
    /**
     * Turns styles collected during renderApp into an array of React elements of
     * HTML <style> tags ready for insertion into the SSR HTML's <head>.
     *
     * Must be called after `renderApp` has completed.
     *
     * @param {...ReactElement} existingStyles - any existing style tag elements to merge in
     * @returns {ReactElement[]} - flat array of <style> React elements
     */
    getStyles: function () {
      if (!hasAppRendered) throw new Error('Called getStyles before renderApp in ssrStyles');
      for (var _len = arguments.length, existingStyles = new Array(_len), _key = 0; _key < _len; _key++) {
        existingStyles[_key] = arguments[_key];
      }
      return [...existingStyles, ...styleGetters.flatMap(getter => getter()), (0, _inject.flush)()];
    }
  };
};
exports.ssrStyles = ssrStyles;
var _default = exports.default = ssrStyles;
/**
 * @deprecated - use ssrStyles instead
 *
 * Registers the app's root component with React Native Web and generates
 * the main <style> tag containing React Native Web stylesheet styles.
 *
 * @param {ReactComponent} AppRoot
 * @param {string} [appName]
 * @returns {ReactElement[]}
 */
const getReactNativeWebSSRStyles = function (AppRoot) {
  let appName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'app';
  _AppRegistry.default.registerComponent(appName, () => AppRoot);
  const {
    getStyleElement
  } = _AppRegistry.default.getApplication(appName);
  return [getStyleElement()];
};

/**
 * @deprecated - use ssrStyles instead
 *
 * Gets style tags for each currently supported CSS-in-JS library and returns
 * them alongside any existing style tags.
 *
 * @param {ReactComponent} AppRoot
 * @param {string} [appName]
 * @param {ReactElement[]} [existingStyles]
 * @returns {ReactElement[]}
 */
exports.getReactNativeWebSSRStyles = getReactNativeWebSSRStyles;
const getSSRStyles = function (AppRoot) {
  let appName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'app';
  let existingStyles = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  return [...existingStyles, ...getReactNativeWebSSRStyles(AppRoot, appName)
  // if any other CSS-in-JS is added e.g. styled-components generate and add its styles here
  ];
};
exports.getSSRStyles = getSSRStyles;